import { useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { updateCalendarEventFilters } from '../action/display-settings/update-calendar-event-filters';
import { updateJobStatusFilters } from '../action/display-settings/update-job-status-filters';
import { userSelector } from '../reducers/current-user-selectors';
import {
  DefaultCalendarEventFilters,
  DefaultJobStatusFilters,
} from '../reducers/display-settings-reducer';
import { calendarEventFilters, jobStatusFilters } from '../reducers/display-settings-selectors';

const mapStateToProps = (state: any) => {
  const user = userSelector(state);
  return {
    username: user?.username || '',
    eventFilters: calendarEventFilters(state, user?.username),
    statusFilters: jobStatusFilters(state, user?.username),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    updateEventFilters: (eventFilters: Array<SelectOption<string>>, username: string) =>
      dispatch(updateCalendarEventFilters(eventFilters, username)),
    updateJobStatusFilters: (statusFilters: Array<SelectOption<string>>, username: string) =>
      dispatch(updateJobStatusFilters(statusFilters, username)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = ReduxProps & DispatchProps;

type FilterToMigrateType = {
  current: Array<SelectOption<string>> | undefined;
  defaults: Array<SelectOption<string>>;
  update: (updatedFilters: Array<SelectOption<string>>, username: string) => void;
};

/*
  When we add or remove an option to our displaySettings filter arrays, any users who have filter settings
  saved to local storage will not receive the changes. To make sure that filter options are respected,
  we will save changes to local storage instead of simply returning the option to the frontend, so the selector
  will return the correct settings in all locations.
*/
export const DefaultDisplaySettingsMigrator = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ username, eventFilters, statusFilters, actions }: Props) => {
  useEffect(() => {
    const filtersToMigrate: Array<FilterToMigrateType> = [
      {
        current: eventFilters,
        defaults: DefaultCalendarEventFilters,
        update: actions.updateEventFilters,
      },
      {
        current: statusFilters,
        defaults: DefaultJobStatusFilters,
        update: actions.updateJobStatusFilters,
      },
    ];

    filtersToMigrate.forEach(({ current, defaults, update }) => {
      const currentFilters = current || [];
      if (username && currentFilters.length !== defaults.length) {
        const updatedFilters = currentFilters.concat(
          defaults.filter(
            (defaultFilter) =>
              !currentFilters.some((currentFilter) => currentFilter.value === defaultFilter.value),
          ),
        );
        update(updatedFilters, username);
      }
    });
  }, [
    actions.updateEventFilters,
    actions.updateJobStatusFilters,
    eventFilters,
    statusFilters,
    username,
  ]);

  return null;
});
